import aptSignal from 'AppointmentBox/signal';
import { ICalendarHeader, ICalendarItemData, IColorSetItem } from 'Calendar/types';
import { Row } from 'antd';
import Icon from 'assets/Icons';
import Text from 'components/Text';
import { APPOINTMENT_SOURCE_TYPE, StatusColorSets } from 'constants/calendar';
import React, { useMemo } from 'react';
import aptActions from 'services/actions/apt';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { IAppointmentItemData } from 'types/appointment';
import { FORMAT_TIME } from 'utils/format';
import { momentTimezone } from 'utils/time';
import DraggableItem from './DnD/DraggableItem';
import { getColorSet } from 'Calendar/helper';
import { zoomLevel } from 'Calendar/styles';
import { verifyPinCode } from 'widgets/ModalVerifyAdminPin';
import { AppointmentStatus } from 'Turn/services/constants';
import { ConfirmIcon, IconCalling, IconOnline, NoRequestStaff, RequestStaffIcon } from 'assets/appointment';

type IAppointmentProps = {
  data: ICalendarItemData;
  isMulti?: boolean;
  headerData?: ICalendarHeader;
  isMonthView?: boolean;
  dateOfMonth?: string;
  disabled?: boolean;
  overlay?: boolean;
};
const Appointment: React.FC<IAppointmentProps> = ({ overlay, disabled: disabledProp, data, isMulti = false, isMonthView, dateOfMonth, headerData }) => {
  const dispatch = useAppDispatch();
  const time = useMemo(() => {
    const startTime = momentTimezone(data?.startTime);
    const endTime = momentTimezone(data?.endTime);
    return `${startTime.format(FORMAT_TIME)} - ${endTime.format(FORMAT_TIME)}`;
  }, [data]);
  const extraData = useMemo(() => {
    return data.data as IAppointmentItemData;
  }, [data]);

  const colorSet = useMemo(() => {
    if (extraData.status === AppointmentStatus.CHECK_IN) return StatusColorSets.checkIn;
    if (extraData.confirmed) return StatusColorSets.confirm;
    return getColorSet(headerData?.typeColor);
  }, [headerData, extraData, data]);

  const disabled = disabledProp || extraData.status === AppointmentStatus.CHECK_IN;

  const addMoreTimeAppointment: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    verifyPinCode.current?.checkAdmin(pinCode => {
      dispatch(aptActions.calendar.prolongedTime({ id: extraData.id, status: 'ADD', pinCode: pinCode || '' }));
    });
  };
  const minusTimeAppointment: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    verifyPinCode.current?.checkAdmin(pinCode => {
      dispatch(aptActions.calendar.prolongedTime({ id: extraData.id, status: 'MINUS', pinCode: pinCode || '' }));
    });
  };

  const handleOpenDetail = () => {
    const startTime = momentTimezone(extraData.startTime);
    const endTime = momentTimezone(extraData.endTime);
    const totalDuration = endTime.diff(startTime, 'minute');
    aptSignal.openDetail({
      id: extraData.id,
      customer: {
        id: extraData.customerId,
        name: extraData.customerName,
        phone: extraData.customerPhone,
      },
      services: extraData.services,
      staffId: extraData.staffId,
      time: startTime.format(),
      totalDuration,
      note: extraData.note,
      isViewOnly: disabled,
      requestStaff: extraData.requestStaff,
      status: extraData.status,
      quickBook: extraData.quickBook,
    });
  };

  const isSmall = useMemo(() => {
    const duration = momentTimezone(data.endTime).diff(momentTimezone(data.startTime), 'minute');
    const isSMZoom = zoomLevel.value !== 0;
    return isSMZoom && duration <= 15;
  }, [extraData, zoomLevel.value]);

  const Content = () => {
    const customerName = extraData.customerName ? (['WALL-IN', 'WALK-IN'].find(o => extraData.customerName?.includes(o)) ? 'Walk-In' : extraData.customerName) : 'Walk-In';
    const duration = momentTimezone(data.endTime).diff(momentTimezone(data.startTime), 'minute');

    const isSmallest = zoomLevel.value === 2;
    if (isSmall) return (
      <>
        <div className={'appointment-box appointment-box-sm'} style={{ padding: '0px 3px', paddingBottom: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '4px' }} className='text-ellipsis'>
            <div style={{ textAlign: 'left', flex: 1 }}>
              <Text className='customerName appointment-name-sm' variant='CONTENT_2' color='text_3' style={{ fontWeight: '600' }}>{customerName?.slice(0, 11)}{customerName.length > 11 ? '...' : ''}</Text>
              {!isSmallest && <RowTime align={'middle'}>
                <Text className='time appointment-time-sm' variant='CAPTION_3' color='text_3' style={{ fontWeight: '600' }}>{time}</Text>
              </RowTime>}
            </div>
            <div>
            </div>
            {extraData.requestStaff ? (<div className='appointment-icon-sm' style={{ display: 'flex', paddingTop: '1px' }}><RequestStaffIcon /></div>) : <div className='appointment-icon-sm' style={{ display: 'flex', paddingTop: '1px' }}><NoRequestStaff /></div>}
            <AppointmentType small className='appointment-icon-sm'>
              {extraData.sourceType !== APPOINTMENT_SOURCE_TYPE.CALl_IN ? <IconOnline /> : <IconCalling />}
            </AppointmentType>
          </div>
          <Text className='text-ellipsis service appointment-service-sm' variant='CAPTION_3' color='text_3' textAlign='left'>
            <b>Sers:</b> {extraData.services.map(o => o.serviceName || '').join(',')}
            {extraData.note && <><b> Notes:</b> {extraData.note}</>}
          </Text>
        </div>
        {!disabled && !isMonthView &&
          <Row className="bottom appointment-bottom-action" align={'middle'} justify={'center'} style={{ gap: isSmallest ? '0.5rem' : '1rem' }}>
            {duration > 15 && <Row align={'middle'} justify={'center'} ><button onClick={minusTimeAppointment}><Icon type='minusAppointment' /></button></Row>}
            <Row align={'middle'} justify={'center'} ><button onClick={addMoreTimeAppointment}><Icon type='addAppointment' /></button></Row>
          </Row>
        }
      </>
    );

    return (
      <>
        <div className={'appointment-box'}>
          <Row style={{ margin: 0 }}>
            <RowTime align={'middle'} style={{ flex: 1 }}>
              <Text className='time appointment-time' variant='CAPTION_3' color='text_3' style={{ fontWeight: '600' }}>{time}</Text>
            </RowTime>
            {extraData.requestStaff ? (<div className='appointment-icon' style={{ opacity: 0 }}><RequestStaffIcon /></div>) : <div style={{ opacity: 0 }} className='appointment-icon'><NoRequestStaff /></div>}
            <div style={{ position: 'absolute', right: '4px', top: '2px', gap: '2px', display: 'flex', alignItems: 'center' }}>
              {extraData.requestStaff ? <div className='appointment-icon'><RequestStaffIcon /></div> : <div className='appointment-icon'><NoRequestStaff /></div>}
            </div>
          </Row>
          {dateOfMonth && <Text variant='BODY_1' color='text_2' mb={1} p={0.25} textAlign='left'>{dateOfMonth}</Text>}
          <div style={{ textAlign: 'left' }}>
            <Text className='customerName appointment-name' variant='CONTENT_2' color='text_3' style={{ fontWeight: '600' }}>{customerName}</Text>
          </div>
          <Text className='service appointment-service' variant='CAPTION_3' color='text_3' style={{ textAlign: 'left' }}>{extraData.services.map(o => o.serviceName || '').join(', ')}</Text>

          {extraData.note && <div style={{ textAlign: 'left' }}>
            <Text className='service appointment-service' variant='CAPTION_3' color='text_3' textAlign='left' >
              <b>Note:</b> {extraData.note}</Text>
          </div>}
          <div className="flex-grow" />
        </div>
         {!isMonthView && <>
          {isMulti || !!overlay ? null : <div style={{
            height: 20,
            width: '100%',
            position: 'absolute',
            bottom: 0,
            backgroundImage: `url(${colorSet.bottomBG})`,
            backgroundColor: colorSet.aptBg,
            boxShadow: `inset 3px 0px 0px 0px ${colorSet.shadow}`,
            borderBottomRightRadius: 5,
            borderBottomLeftRadius: 5,
            borderBottom: `0.5px solid ${colorSet.shadow}`,
          }}></div>}
        </>}
        {!disabled && !isMonthView &&
          <>
            <Row className="bottom appointment-bottom-action" align={'middle'} justify={'center'} style={{
              gap: isSmallest ? '0.5rem' : '1rem',
            }}>
              {duration > 15 && <Row align={'middle'} justify={'center'} ><button onClick={minusTimeAppointment}><Icon type='minusAppointment' /></button></Row>}
              <Row align={'middle'} justify={'center'} ><button onClick={addMoreTimeAppointment}><Icon type='addAppointment' /></button></Row>
            </Row>
          </>
        }
        <AppointmentType isMulti={isMulti} small className='appointment-icon'>
          {extraData.sourceType !== APPOINTMENT_SOURCE_TYPE.CALl_IN ? <IconOnline /> : <IconCalling />}
        </AppointmentType>
      </>
    );
  };

  if (extraData.status === AppointmentStatus.CHECK_IN) {
    return (
      <AppointmentStyled
        className='AppointmentStyled'
        isAnybody={extraData.isAnybody || extraData.requestStaff}
        isMulti={isMulti}
        colorSet={colorSet}
        $statusMode={extraData.status === AppointmentStatus.CHECK_IN}
        onClick={((e) => [e.stopPropagation(), handleOpenDetail()]) as React.MouseEventHandler<HTMLDivElement>}
      >
        <Content />
      </AppointmentStyled>
    );
  }

  return (
    <DraggableItem
      key={data?.id}
      id={data?.id?.toString()}
      data={{ data }}
    >
      <AppointmentStyled
        className='AppointmentStyled'
        isAnybody={extraData.isAnybody || extraData.requestStaff}
        isMulti={isMulti}
        colorSet={colorSet}
        $statusMode={extraData.confirmed}
        onClick={((e) => [e.stopPropagation(), handleOpenDetail()]) as React.MouseEventHandler<HTMLDivElement>}
      >
        <Content />
      </AppointmentStyled>
    </DraggableItem >
  );
};

export default Appointment;
type AppointmentStyledProps = {
  isMulti: boolean;
  isAnybody?: boolean;
  $statusMode?: boolean;
  colorSet?: IColorSetItem | null;
}
const AppointmentStyled = styled.div<AppointmentStyledProps>`
  height: calc(100% - 12px);
  width: 100%;
  display: flex;
  position: relative;
  align-self: stretch;
  
  .appointment-box {
    background: #fff;
    border-radius: 5px;
    overflow: hidden;

    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 0px 4px;
    padding-bottom: 1.25rem;
    flex: 1;

    ${({ colorSet }) => colorSet ? `
      border-radius: 5px;
      background: ${colorSet.aptBg};
      border: 0.5px solid ${colorSet.shadow};
      border-bottom: 0;
      box-shadow: inset 3px 0px 0px 0px ${colorSet.shadow};
    `: ''}

    ${({ isMulti }) => isMulti ? `
      border-radius: 5px;
      background: #FFFFFF;
      border: 0.5px solid #95B8D1;
      box-shadow: inset 3px 0px 0px 0px #0D446C;
      padding: 4px 8px;
    `: ''}
    .flex-grow {
      flex:1;
    }


    ${({ $statusMode, colorSet }) => $statusMode ? `
      .appointment-time, .appointment-service, .appointment-name {
        color: ${colorSet?.text || '#fff'}
      }
    `: ''}
  }
  .apt-online {
    background: #E7FBFF;
  }
  .bottom {
    position: absolute;
    bottom:0;
    width:100%;
    transform: translateY(16px);
    button {
      border-radius: 100%;
    }
  }

  .customerName {
    color: #1D2129;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }

  .service {
    overflow: hidden;
    color: #1D2129;
    text-overflow: ellipsis;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
`;

const RowTime = styled(Row)`
  gap: 4px;
  .time {
    color: #7B7B7B;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

// const TimeLock = () => (
//   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
//     <path d="M10 18.75C8.26942 18.75 6.57769 18.2368 5.13876 17.2754C3.69983 16.3139 2.57832 14.9473 1.91606 13.3485C1.25379 11.7496 1.08051 9.9903 1.41813 8.29296C1.75575 6.59563 2.58911 5.03653 3.81282 3.81282C5.03653 2.58911 6.59563 1.75575 8.29296 1.41813C9.9903 1.08051 11.7496 1.25379 13.3485 1.91606C14.9473 2.57832 16.3139 3.69983 17.2754 5.13876C18.2368 6.57769 18.75 8.26942 18.75 10C18.75 12.3206 17.8281 14.5462 16.1872 16.1872C14.5462 17.8281 12.3206 18.75 10 18.75ZM10 2.5C8.51664 2.5 7.0666 2.93987 5.83323 3.76398C4.59986 4.58809 3.63856 5.75943 3.07091 7.12988C2.50325 8.50032 2.35473 10.0083 2.64411 11.4632C2.9335 12.918 3.64781 14.2544 4.6967 15.3033C5.7456 16.3522 7.08197 17.0665 8.53683 17.3559C9.99168 17.6453 11.4997 17.4968 12.8701 16.9291C14.2406 16.3614 15.4119 15.4002 16.236 14.1668C17.0601 12.9334 17.5 11.4834 17.5 10C17.5 8.01088 16.7098 6.10323 15.3033 4.6967C13.8968 3.29018 11.9891 2.5 10 2.5Z" fill="#F05326" />
//     <path d="M12.8688 13.75L9.375 10.2563V4.375H10.625V9.7375L13.75 12.8688L12.8688 13.75Z" fill="#F05326" />
//   </svg>
// );

const AppointmentType = styled.div<{ small?: boolean, isMulti?: boolean }>`
  position: absolute;
  right: 8px;
  top: 4px;
  gap: 4px;
  display: flex;
  align-items: center;
  ${({ small }) => small && `
  position: absolute;
  right: 4px;
  top:unset;
  bottom: 2px;
  gap: 2px;
    svg {
      width: 24px !important;
    }
  `}

  ${({ isMulti }) => isMulti && `
    top: 4px;
    gap: 4px;
    bottom: unset;
  `}
`;
